import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  TextField,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import TitleWithBack from "../Home/TitleWithBack";
import {
  align_center,
  align_right,
  align_row,
  VM_Colors,
} from "../../Styles/VMStyles";
import MUITypography from "../../Components/MUITypography";
import { useNavigate } from "react-router-dom";
import { API_ENQUIRY_UPDATE_STATUS } from "../../Util/URLConstants";
import { toast } from "react-toastify";
import StatusButton from "../../Components/StatusButton";
import {
  ROUTE_ADD_ENQUIRY,
  SESSION_USER_ID_KEY,
  STATUS_CONFIRMED,
  STATUS_LEAD,
  STATUS_NOT_INTERESTED,
  UPDATE,
} from "../../Util/VMConstants";
import StorageFactory from "../../Util/StorageFactory";
import MUIAlertDialog from "../../Components/MUIAlertDialog";
import ReusableDialog from "../../Components/ReusableDialog";

function VMEDHeader({
  customer,
  events,
  refreshEnquiryDetails,
  confirmedStatusUpdated,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertDialogMessage,setAlertDialogMessage] = useState('');

  const [status, setStatus] = useState(customer.appointment_status);

  // For Not Interested Reason
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  useEffect(() => {
    setStatus(customer.appointment_status);
  }, [customer.appointment_status]);

  const handleUpdateStatus = async () => {
    // If Not Interested but no reason yet → open reason dialog
    if (status === STATUS_NOT_INTERESTED && !selectedReason) {
      setReasonDialogOpen(true);
      return;
    }

    // Determine final reason (predefined or custom)
    let finalReason = null;
    if (status === STATUS_NOT_INTERESTED) {
      if (selectedReason === "Others") {
        if (!customReason.trim()) {
          toast.error("Please enter a custom reason.");
          return;
        }
        finalReason = `Other: ${customReason.trim()}`;
      } else {
        finalReason = selectedReason;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(API_ENQUIRY_UPDATE_STATUS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          enquiry_id: customer.appointment_id,
          appointment_status: status,
          user_id: userID,
          reason: finalReason,
        }),
      });

      const data = await res.json();
      setLoading(false);
      if (data.status_code === 200) {
        toast.success("Status updated successfully!");
        if (status === STATUS_CONFIRMED) {
          const hasValidRooms = events.some((item) => {
            const rooms = item.accomodation_no_of_rooms;
            return rooms !== "" && Number(rooms) > 0;
          });
          if (hasValidRooms) {
            setDialogOpen(true);
          }
          handleClose();
        } else {
          handleClose();
        }
        refreshEnquiryDetails();
      } else {
        setAlertDialogOpen(true);
        setAlertDialogMessage(data.message || "Failed to update status");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      toast.error("Network or server error");
    }
  };

  const handleChange = (event) => {
    setStatus(event.target.value);
    if (event.target.value !== STATUS_NOT_INTERESTED) {
      setSelectedReason("");
      setCustomReason("");
    }
  };

  const handleOpen = () => {
    if (events.length > 0) {
      setStatus(customer.appointment_status); 
      setOpen(true);
    } else {
      toast.error("This enquiry doesn't have events. Please add the events");
    }
  };

  const handleClose = () => setOpen(false);

  const descriptions = (sts) => {
    switch (sts) {
      case STATUS_LEAD:
        return "By default, Enquiry Person details will be saved as Lead.";
      case STATUS_CONFIRMED:
        return "Select this option when person confirms Booking.";
      case STATUS_NOT_INTERESTED:
        return "Select this option when person rejects Booking.";
      default:
        return "";
    }
  };

  return (
    <Box sx={align_row}>
      <TitleWithBack
        title="Enquiry Details"
        onBackClick={readOnly ? undefined : () => navigate(-1)}
      />

      <Box sx={{ width: "100%", ...align_right }}>
        {
          !readOnly && customer.appointment_status.toLowerCase() === 'lead'
          ?  <Box sx={align_center}>
                <Tooltip title="Update Enquiry Details" arrow>
                <Avatar
                  alt="Room"
                  src={'/static/update.svg'}
                  sx={{
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    padding: 1,
                    mr: 5,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    navigate("/" + ROUTE_ADD_ENQUIRY, {
                      state: { enquiry_id: customer.appointment_id, arrived: UPDATE },
                    });
                  }}
                />
                </Tooltip>
              </Box>
          :null
        }
        
        <Box
          sx={{
            border: "1px solid gray",
            borderRadius: "10px",
            padding: 2,
            ...align_row,
          }}
        >
          <MUITypography sx={align_center}>Customer Status:</MUITypography>
           <Tooltip title={readOnly ? "" : "Update Enquiry Status"} arrow>
          <Box ml={2} sx={{ ...align_center, pointerEvents: readOnly ? "none" : "auto" }}>
            <StatusButton
              status={customer.appointment_status}
              handleUpdateStatus={readOnly ? undefined : handleOpen}
            />
          </Box>
         </Tooltip>
          {!readOnly ? <Box sx={align_center}>
            <Avatar
              alt="Room"
              src={'/static/updownarrows.svg'}
              sx={{
                width: 10,
                height: 20,
                backgroundColor: "transparent",
                boxShadow: "none",
                padding: 1,
              }}
            />
          </Box> : null}
        </Box>
      </Box>

      {/* Status Selection Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <MUITypography sx={align_center}>Add Status</MUITypography>
        </DialogTitle>
        <DialogContent>
          <RadioGroup value={status} onChange={handleChange}>
            {[STATUS_LEAD, STATUS_CONFIRMED, STATUS_NOT_INTERESTED].map(
              (option) => (
                <Box key={option} sx={{ mb: 2 }}>
                  <FormControlLabel
                    value={option}
                    control={<Radio  disabled ={option === 'Lead' && customer.appointment_status.toLowerCase() === 'confirmed' ? true : false } />}
                    label={option}
                  />
                  <Typography color={VM_Colors.black} sx={{ pl: 4 }}>
                    {descriptions(option)}
                  </Typography>
                </Box>
              )
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleUpdateStatus}
            loading={loading}
            loadingPosition="end"
          >
            Update Status
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Confirmed → Book Rooms Alert */}
      <MUIAlertDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        message={"Enquiry is Confirmed. Please book the rooms."}
        okClickHandler={() => {
          confirmedStatusUpdated();
        }}
      />

      {/* Not Interested Reasons Dialog */}
      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <MUITypography sx={align_center}>
            Why is the customer not interested?
          </MUITypography>
        </DialogTitle>
        <DialogContent>
          <RadioGroup
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
          >
            {[
              "High Pricing – The venue package is out of their budget.",
              "Limited Availability – Desired dates are already booked or not flexible.",
              "Location Issues – Venue is too far.",
              "Vendor Restrictions – Customers prefer their own vendors, but venue enforces in-house vendors.",
              "Change in Event Plan – Postponement, cancellation, or downsizing of event.",
              "Others",
            ].map((reason, index) => (
              <FormControlLabel
                key={index}
                value={reason}
                control={<Radio />}
                label={reason}
              />
            ))}
          </RadioGroup>

          {selectedReason === "Others" && (
            <Box mt={2}>
              <TextField
                fullWidth
                label="Please specify"
                variant="outlined"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={() => {
              setReasonDialogOpen(false);
              handleUpdateStatus();
            }}
          >
            Confirm
          </LoadingButton>
        </DialogActions>
      </Dialog>

     <ReusableDialog
        open={alertDialogOpen}
        onClose={() => {setAlertDialogOpen(false); handleClose(); } }
        title="Alert"
        message={alertDialogMessage}
      />

    </Box>
  );
}

export default VMEDHeader;
